import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';

const MEAL_COLORS_HEX = { breakfast: '#f59e0b', lunch: '#10b981', dinner: '#6366f1' };
const CATEGORY_COLORS_HEX = { student: '#0ea5e9', resident: '#8b5cf6', staff: '#f43f5e', visitor: '#f97316' };

export default function MealTicketReports({ isAdmin }) {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date(); d?.setDate(d?.getDate() - 30);
    return d?.toISOString()?.split('T')?.[0];
  });
  const [dateTo, setDateTo] = useState(new Date()?.toISOString()?.split('T')?.[0]);
  const [filterMeal, setFilterMeal] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => { fetchData(); }, [dateFrom, dateTo, filterMeal, filterCategory, filterStatus]);

  const fetchData = async () => {
    setLoading(true);
    try {
      let query = supabase
        ?.from('restaurant_meal_tickets')
        ?.select('*, restaurant_cardholders(full_name, category, building, department)')
        ?.gte('valid_date', dateFrom)
        ?.lte('valid_date', dateTo)
        ?.order('valid_date', { ascending: false });
      if (filterMeal !== 'all') query = query?.eq('meal_type', filterMeal);
      if (filterStatus !== 'all') query = query?.eq('status', filterStatus);
      const { data, error } = await query;
      let filtered = data || [];
      if (filterCategory !== 'all') {
        filtered = filtered?.filter(t => t?.restaurant_cardholders?.category === filterCategory);
      }
      if (!error) setTickets(filtered);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  // Stats
  const total = tickets?.length;
  const used = tickets?.filter(t => t?.status === 'used')?.length;
  const active = tickets?.filter(t => t?.status === 'active')?.length;
  const expired = tickets?.filter(t => t?.status === 'expired')?.length;
  const usageRate = total > 0 ? Math.round((used / total) * 100) : 0;

  // Meal type breakdown
  const mealBreakdown = ['breakfast', 'lunch', 'dinner']?.map(m => ({
    name: m?.charAt(0)?.toUpperCase() + m?.slice(1),
    total: tickets?.filter(t => t?.meal_type === m)?.length,
    used: tickets?.filter(t => t?.meal_type === m && t?.status === 'used')?.length,
    fill: MEAL_COLORS_HEX?.[m],
  }));

  // Category breakdown for pie
  const categoryBreakdown = ['student', 'resident', 'staff', 'visitor']?.map(cat => ({
    name: cat?.charAt(0)?.toUpperCase() + cat?.slice(1),
    value: tickets?.filter(t => t?.restaurant_cardholders?.category === cat)?.length,
    fill: CATEGORY_COLORS_HEX?.[cat],
  }))?.filter(c => c?.value > 0);

  // Daily trend (last 14 days in range)
  const dailyMap = {};
  tickets?.forEach(t => {
    const d = t?.valid_date;
    if (!dailyMap?.[d]) dailyMap[d] = { date: d, total: 0, used: 0 };
    dailyMap[d].total++;
    if (t?.status === 'used') dailyMap[d].used++;
  });
  const dailyTrend = Object.values(dailyMap)?.sort((a, b) => a?.date?.localeCompare(b?.date))?.slice(-14);

  const statCards = [
    { label: 'Total Tickets', value: total, icon: 'Ticket', color: 'text-amber-600', bg: 'bg-amber-50' },
    { label: 'Meals Served', value: used, icon: 'CheckCircle2', color: 'text-emerald-600', bg: 'bg-emerald-50' },
    { label: 'Active Tickets', value: active, icon: 'Clock', color: 'text-sky-600', bg: 'bg-sky-50' },
    { label: 'Usage Rate', value: `${usageRate}%`, icon: 'TrendingUp', color: 'text-violet-600', bg: 'bg-violet-50' },
  ];

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-card border border-border rounded-xl p-4">
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">From</label>
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e?.target?.value)}
              className="border border-border rounded-lg px-3 py-1.5 text-xs bg-background text-foreground focus:outline-none" />
          </div>
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">To</label>
            <input type="date" value={dateTo} onChange={e => setDateTo(e?.target?.value)}
              className="border border-border rounded-lg px-3 py-1.5 text-xs bg-background text-foreground focus:outline-none" />
          </div>
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">Meal Type</label>
            <select value={filterMeal} onChange={e => setFilterMeal(e?.target?.value)}
              className="border border-border rounded-lg px-3 py-1.5 text-xs bg-background text-foreground focus:outline-none">
              <option value="all">All Meals</option>
              <option value="breakfast">Breakfast</option>
              <option value="lunch">Lunch</option>
              <option value="dinner">Dinner</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">Category</label>
            <select value={filterCategory} onChange={e => setFilterCategory(e?.target?.value)}
              className="border border-border rounded-lg px-3 py-1.5 text-xs bg-background text-foreground focus:outline-none">
              <option value="all">All Categories</option>
              <option value="student">Students</option>
              <option value="resident">Residents</option>
              <option value="staff">Staff</option>
              <option value="visitor">Visitors</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">Status</label>
            <select value={filterStatus} onChange={e => setFilterStatus(e?.target?.value)}
              className="border border-border rounded-lg px-3 py-1.5 text-xs bg-background text-foreground focus:outline-none">
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="used">Used</option>
              <option value="expired">Expired</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          <button onClick={fetchData} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-500 text-white text-xs font-semibold hover:bg-amber-600 transition-all">
            <Icon name="RefreshCw" size={12} />Apply
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16"><Icon name="Loader2" size={24} className="animate-spin text-muted-foreground" /></div>
      ) : (
        <>
          {/* Stat Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {statCards?.map((s, i) => (
              <div key={i} className="bg-card border border-border rounded-xl p-4 flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl ${s?.bg} flex items-center justify-center flex-shrink-0`}>
                  <Icon name={s?.icon} size={18} className={s?.color} />
                </div>
                <div>
                  <div className="text-xl font-bold text-foreground">{s?.value}</div>
                  <div className="text-xs text-muted-foreground">{s?.label}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Meal Type Bar Chart */}
            <div className="bg-card border border-border rounded-xl p-5">
              <h4 className="font-semibold text-foreground text-sm mb-4 flex items-center gap-2">
                <Icon name="BarChart3" size={15} className="text-amber-500" />
                Meals by Type
              </h4>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={mealBreakdown} barGap={4}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                  <Bar dataKey="total" name="Total" radius={[4, 4, 0, 0]}>
                    {mealBreakdown?.map((entry, i) => <Cell key={i} fill={entry?.fill} fillOpacity={0.5} />)}
                  </Bar>
                  <Bar dataKey="used" name="Used" radius={[4, 4, 0, 0]}>
                    {mealBreakdown?.map((entry, i) => <Cell key={i} fill={entry?.fill} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Category Pie */}
            <div className="bg-card border border-border rounded-xl p-5">
              <h4 className="font-semibold text-foreground text-sm mb-4 flex items-center gap-2">
                <Icon name="PieChart" size={15} className="text-violet-500" />
                Tickets by Category
              </h4>
              {categoryBreakdown?.length > 0 ? (
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie data={categoryBreakdown} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} label={({ name, percent }) => `${name} ${(percent * 100)?.toFixed(0)}%`} labelLine={false}>
                      {categoryBreakdown?.map((entry, i) => <Cell key={i} fill={entry?.fill} />)}
                    </Pie>
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                    <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-48 text-muted-foreground text-sm">No data available</div>
              )}
            </div>
          </div>

          {/* Daily Trend */}
          {dailyTrend?.length > 0 && (
            <div className="bg-card border border-border rounded-xl p-5">
              <h4 className="font-semibold text-foreground text-sm mb-4 flex items-center gap-2">
                <Icon name="TrendingUp" size={15} className="text-emerald-500" />
                Daily Meal Consumption
              </h4>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={dailyTrend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={d => d?.slice(5)} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} labelFormatter={d => `Date: ${d}`} />
                  <Bar dataKey="total" name="Generated" fill="#f59e0b" fillOpacity={0.4} radius={[3, 3, 0, 0]} />
                  <Bar dataKey="used" name="Served" fill="#10b981" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Ticket Log Table */}
          <div className="bg-card border border-border rounded-xl p-5 space-y-3">
            <h4 className="font-semibold text-foreground text-sm flex items-center gap-2">
              <Icon name="ClipboardList" size={15} className="text-muted-foreground" />
              Ticket Log ({tickets?.length})
            </h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-2 px-3 text-xs font-semibold text-muted-foreground">Ticket #</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-muted-foreground">Name</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-muted-foreground">Category</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-muted-foreground">Meal</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-muted-foreground">Date</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-muted-foreground">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {tickets?.slice(0, 50)?.map(t => {
                    const statusMap = { active: 'bg-emerald-100 text-emerald-700', used: 'bg-slate-100 text-slate-600', expired: 'bg-red-100 text-red-600', cancelled: 'bg-orange-100 text-orange-600' };
                    return (
                      <tr key={t?.id} className="hover:bg-muted/40 transition-colors">
                        <td className="py-2 px-3 font-mono text-xs text-muted-foreground">{t?.ticket_number?.slice(-12)}</td>
                        <td className="py-2 px-3 font-medium text-foreground">{t?.restaurant_cardholders?.full_name}</td>
                        <td className="py-2 px-3 text-muted-foreground capitalize">{t?.restaurant_cardholders?.category}</td>
                        <td className="py-2 px-3 capitalize text-foreground">{t?.meal_type}</td>
                        <td className="py-2 px-3 text-muted-foreground">{t?.valid_date}</td>
                        <td className="py-2 px-3">
                          <span className={`text-xs px-2 py-0.5 rounded font-medium ${statusMap?.[t?.status] || 'bg-slate-100 text-slate-600'}`}>
                            {t?.status}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {tickets?.length === 0 && (
                <div className="py-10 text-center text-sm text-muted-foreground">No tickets found for the selected filters</div>
              )}
              {tickets?.length > 50 && (
                <div className="py-2 text-center text-xs text-muted-foreground">Showing first 50 of {tickets?.length} records</div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
